import React, { useContext, useState, useEffect, Fragment, useCallback, useMemo } from 'react';
import Layout from '../../components/layout/main';
import { makeStyles } from 'tss-react/mui';
import { Fade, Grid, Paper } from '@mui/material';
import * as yup from 'yup';
import { postData, getData, putData, getDataFull } from '../../services/ajax';
import { AuthContext } from '../../context/auth';
import { LoadingModalContext } from '../../components/modal/loading';
import { AlertModalContext } from '../../components/modal/alert';
import { setYupLocaleES } from '../../const/yup';
import { useNavigate } from 'react-router-dom';
import { CustomForm } from '../../components/formSections';
import ProcessGuide from '../../components/orden/ProcessGuide';
import { GlobalsContext } from '../../context/globals';
import { useSnackbar } from 'notistack';

setYupLocaleES();

const useStyles = makeStyles()((theme) => ({
    paper: {
        padding: theme.spacing(4),
        paddingBottom: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        width: '100%',
        marginBottom: theme.spacing(2),
    },
}));

function Page() {
    const { classes } = useStyles();
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const globalsContext = useContext(GlobalsContext);
    const [data, setData] = useState(null);
    const [establecimientosList, setEstablecimientosList] = useState([]);

    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);
    const { enqueueSnackbar } = useSnackbar();

    const onSubmit = async (values) => {
        loadingModal.setOpen(true);

        try {
            const establecimientoIds = Array.isArray(values.establecimientos_detalle)
                ? [...new Set(values.establecimientos_detalle.map((r) => Number(r.establecimiento_id)))]
                : [];
            const payload = {
                nombre: values.nombre,
                activo: Boolean(values.activo),
                plazo_respuesta_minutos: Number(values.plazo_respuesta_minutos),
                plazo_max_sin_turno_minutos: Number(values.plazo_max_sin_turno_minutos),
                requiere_insumo: Boolean(values.requiere_insumo),
                plazo_compra_horas: Number(values.plazo_compra_horas),
                reprogramaciones_max: Number(values.reprogramaciones_max),
                reprogramacion_antelacion_horas: Number(values.reprogramacion_antelacion_horas),
                reprogramacion_cooldown_horas: Number(values.reprogramacion_cooldown_horas),
                establecimiento_ids: establecimientoIds,
            };

            let response;
            if (data && data.id) {
                response = await putData(`practicas/${data.id}`, payload, usuario.token);
            } else {
                response = await postData('practicas', payload, usuario.token);
            }

            if (response.error === '') {
                enqueueSnackbar(
                    data ? 'Práctica modificada correctamente' : 'Práctica creada correctamente',
                    { variant: 'success' }
                );
                onClose();
            } else {
                enqueueSnackbar(response.error, { variant: 'error' });
            }
        } catch (error) {
            enqueueSnackbar('Error al guardar la práctica', { variant: 'error' });
        } finally {
            loadingModal.setOpen(false);
        }
    };

    const onClose = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const onCancel = () => {
        alertModal.showModal(
            'Cancelar',
            'Se perderán todos los cambios. ¿Deseas continuar?',
            () => onClose()
        );
    };

    const loadData = useCallback(async () => {
        if (globalsContext.idEdit) {
            loadingModal.setOpen(true);
            try {
                const response = await getData(`practicas/${globalsContext.idEdit}`, usuario.token);
                if (response.error === '') {
                    setData(response.data);
                } else {
                    enqueueSnackbar(response.error, { variant: 'error' });
                }
            } catch (error) {
                enqueueSnackbar('Error al cargar la práctica', { variant: 'error' });
            } finally {
                loadingModal.setOpen(false);
            }
        }
    }, [globalsContext.idEdit, usuario.token]);

    useEffect(() => {
        const loadEstablecimientos = async () => {
            const response = await getDataFull('establecimientos?per_page=500', usuario.token);
            if (response.error === '' && response.data) setEstablecimientosList(response.data);
        };
        loadEstablecimientos();
    }, [usuario.token]);

    useEffect(() => {
        if (globalsContext.idEdit) {
            loadData();
        }
    }, [globalsContext.idEdit, loadData]);

    const establecimientoItems = useMemo(
        () => establecimientosList.map((e) => ({ id: e.id, nombre: e.localidad_nombre ? `${e.nombre} (${e.localidad_nombre})` : e.nombre })),
        [establecimientosList]
    );

    const formSections = useMemo(() => [
        {
            name: 'Datos de la práctica',
            description: 'Configurar plazos, límites de reprogramación e insumos. Los límites aplican a cada orden creada con esta práctica y quedan registrados en el historial.',
            fields: [
                {
                    dataName: 'nombre',
                    label: 'Nombre',
                    controlType: 'textField',
                    initialValue: data ? data.nombre : '',
                    validationSchema: yup.string().required('El nombre es requerido').max(255, 'Máximo 255 caracteres'),
                    autoFocus: true,
                },
                {
                    dataName: 'activo',
                    label: 'Activa',
                    controlType: 'checkBox',
                    initialValue: data ? data.activo !== false : true,
                    validationSchema: yup.boolean(),
                },
                {
                    dataName: 'plazo_respuesta_minutos',
                    label: 'Plazo de respuesta (minutos)',
                    controlType: 'textField',
                    initialValue: data ? data.plazo_respuesta_minutos : 60,
                    validationSchema: yup.number().required('Requerido').min(1).max(525600),
                },
                {
                    dataName: 'plazo_max_sin_turno_minutos',
                    label: 'Plazo máximo sin turno (minutos)',
                    controlType: 'textField',
                    initialValue: data ? data.plazo_max_sin_turno_minutos : 1440,
                    validationSchema: yup.number().required('Requerido').min(1).max(1051200),
                },
                {
                    dataName: 'requiere_insumo',
                    label: 'Requiere compra de insumo',
                    controlType: 'checkBox',
                    initialValue: data ? Boolean(data.requiere_insumo) : false,
                    validationSchema: yup.boolean(),
                },
                {
                    dataName: 'plazo_compra_horas',
                    label: 'Plazo de compra de insumo (horas)',
                    controlType: 'textField',
                    initialValue: data ? data.plazo_compra_horas : 0,
                    validationSchema: yup.number().required('Requerido').min(0).max(8760),
                },
                {
                    dataName: 'reprogramaciones_max',
                    label: 'Máximo de reprogramaciones',
                    controlType: 'textField',
                    initialValue: data ? data.reprogramaciones_max : 2,
                    helperText: 'Cantidad máxima de reprogramaciones por orden. 0 = reprogramación deshabilitada.',
                    validationSchema: yup.number().required('Requerido').min(0).max(100),
                },
                {
                    dataName: 'reprogramacion_antelacion_horas',
                    label: 'Antelación mínima (horas)',
                    controlType: 'textField',
                    initialValue: data ? data.reprogramacion_antelacion_horas : 24,
                    helperText: 'Horas mínimas de anticipación respecto del turno vigente. 0 = sin restricción de antelación.',
                    validationSchema: yup.number().required('Requerido').min(0).max(8760),
                },
                {
                    dataName: 'reprogramacion_cooldown_horas',
                    label: 'Intervalo entre reprogramaciones (horas)',
                    controlType: 'textField',
                    initialValue: data ? data.reprogramacion_cooldown_horas : 12,
                    helperText: 'Horas mínimas entre reprogramaciones consecutivas. 0 = sin intervalo mínimo.',
                    validationSchema: yup.number().required('Requerido').min(0).max(8760),
                },
                {
                    dataName: 'establecimientos_detalle',
                    label: 'Establecimientos que realizan esta práctica',
                    controlType: 'detalleList',
                    initialValue: data && data.establecimiento_ids ? data.establecimiento_ids.map((id) => ({ establecimiento_id: id })) : [],
                    listFields: [
                        {
                            dataName: 'establecimiento_id',
                            label: 'Establecimiento',
                            controlType: 'select',
                            items: establecimientoItems,
                            visibleType: 1,
                            initialValue: '',
                            validationSchema: yup.number().required('Seleccione un establecimiento'),
                        },
                    ],
                    labelPopup: 'establecimiento',
                    labelPopupGenero: 'o',
                    validationSchema: yup.array(),
                },
            ],
        },
    ], [data, establecimientoItems]);

    const formInitialValues = useMemo(() => ({
        nombre: data ? data.nombre : '',
        activo: data ? data.activo !== false : true,
        plazo_respuesta_minutos: data ? data.plazo_respuesta_minutos : 60,
        plazo_max_sin_turno_minutos: data ? data.plazo_max_sin_turno_minutos : 1440,
        requiere_insumo: data ? Boolean(data.requiere_insumo) : false,
        plazo_compra_horas: data ? data.plazo_compra_horas : 0,
        reprogramaciones_max: data ? data.reprogramaciones_max : 2,
        reprogramacion_antelacion_horas: data ? data.reprogramacion_antelacion_horas : 24,
        reprogramacion_cooldown_horas: data ? data.reprogramacion_cooldown_horas : 12,
        establecimientos_detalle: data && data.establecimiento_ids ? data.establecimiento_ids.map((id) => ({ establecimiento_id: id })) : [],
    }), [data]);

    return (
        <Fragment>
            <Layout nombrePagina={data ? 'Editar práctica' : 'Nueva práctica'}>
                <Fade in={true} timeout={900}>
                    <Grid size={12}>
                        <ProcessGuide
                            title="Cómo completar la práctica"
                            steps={[
                                'Definir plazos de respuesta y máximo sin turno (en minutos).',
                                'Si requiere insumo, el plazo 0 h indica urgencia (no bloquea la asignación).',
                                'Definir límites de reprogramación: máximo por orden, antelación e intervalo mínimo.',
                                'Asociar los establecimientos que pueden realizarla.',
                            ]}
                        />
                        <Paper className={classes.paper}>
                            <CustomForm
                                key={data ? `p-${data.id}` : 'p-new'}
                                formSections={formSections}
                                initialValues={formInitialValues}
                                onSubmit={onSubmit}
                                onCancel={onCancel}
                                submitButtonText={data ? 'Guardar cambios' : 'Crear práctica'}
                                cancelButtonText="Cancelar"
                                stepperType="vertical"
                                isModal={false}
                            />
                        </Paper>
                    </Grid>
                </Fade>
            </Layout>
        </Fragment>
    );
}

export default Page;
